import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../../components/DailyBudgetInvoiceItemsTable/aggridCustom.css";
import React, { useCallback, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, CellValueChangedEvent, ValueParserParams } from "ag-grid-community";
import { Alert, Box, Button, IconButton, Snackbar, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import {
  DELIVERY_CONFIG_ID,
  useGetDeliveryZonesAdminQuery,
  useCreateDeliveryZoneMutation,
  useUpdateDeliveryZoneMutation,
  useDeleteDeliveryZoneMutation,
  useSeedDeliveryZonesMutation,
} from "../../services/deliveryZones/deliveryZones";
import { DeliveryConfig, DeliveryZone } from "../../utils/deliveryQuote";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import { PageContentBox } from "../shared/styledComponents";
import { DELIVERY_ZONE_TEXTS, SNACKBAR_AUTO_HIDE_MS } from "../shared/constants";
import { formatCOP } from "../../utils/format";
import { GREEN, RED } from "../../styles/mui-theme";
import DeliveryConfigCard from "./DeliveryConfigCard";
import CreateZoneDialog from "./CreateZoneDialog";
import DeleteZoneDialog from "./DeleteZoneDialog";

const TABLE_HEIGHT = "560px";
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;

/** An empty cell means "no threshold in this municipality", which is not the same as a fee of 0. */
const EMPTY_CELL = "—";

const toNumber = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const numberParser = (params: ValueParserParams) => toNumber(params.newValue);

/** Empty stays `null`; `0` stays `0`. The server tells the two apart and so must the grid. */
const nullableNumberParser = (params: ValueParserParams) => {
  const raw = params.newValue;
  if (raw === null || raw === undefined || String(raw).trim() === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const DeliveryZoneListPage: React.FC = () => {
  useUpdateAuthToken();

  const { data, isFetching, isError } = useGetDeliveryZonesAdminQuery();
  const [createZone, { isLoading: isCreating }] = useCreateDeliveryZoneMutation();
  const [updateZone] = useUpdateDeliveryZoneMutation();
  const [deleteZone, { isLoading: isDeleting }] = useDeleteDeliveryZoneMutation();
  const [seedZones, { isLoading: isSeeding }] = useSeedDeliveryZonesMutation();

  const [searchText, setSearchText] = useState("");
  const [editedRows, setEditedRows] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  // Tracked here rather than read off a second instance of the update mutation: the settings card
  // and the grid rows go through the same endpoint, and one shared `isLoading` would put the card
  // into "Guardando..." every time a municipality was saved.
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<DeliveryZone | null>(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const rowData = useMemo(
    () => (isFetching || !data ? undefined : data.zones.map((zone) => ({ ...zone }))),
    [isFetching, data],
  );

  const zoneOptions = useMemo(
    () => Array.from(new Set((data?.zones ?? []).map((zone) => zone.zone).filter(Boolean))).sort(),
    [data],
  );

  const handleSaveRow = useCallback(
    async (zone: DeliveryZone) => {
      if (!zone.id) return;
      try {
        await updateZone({
          id: zone.id,
          patch: {
            municipality: zone.municipality,
            zone: zone.zone,
            // Coerced here rather than trusted from the grid: the Lambda rejects a `fee_cop` that
            // arrives as a string, and an editor that has never been opened returns the loaded
            // value untouched while one that has returns whatever the parser made of it.
            fee_cop: toNumber(zone.fee_cop),
            free_shipping_threshold_cop:
              zone.free_shipping_threshold_cop === null ||
              zone.free_shipping_threshold_cop === undefined
                ? null
                : toNumber(zone.free_shipping_threshold_cop),
            eta_days_min: toNumber(zone.eta_days_min),
            eta_days_max: toNumber(zone.eta_days_max),
            display_order: toNumber(zone.display_order),
            is_active: zone.is_active === true,
          },
        }).unwrap();

        setEditedRows((prev) => {
          const next = new Set(prev);
          next.delete(zone.id);
          return next;
        });
        setSuccessMsg(DELIVERY_ZONE_TEXTS.saveSuccess);
      } catch {
        setErrorMsg(DELIVERY_ZONE_TEXTS.saveError);
      }
    },
    [updateZone],
  );

  const columnDefs: ColDef<DeliveryZone>[] = useMemo(
    () => [
      { headerName: DELIVERY_ZONE_TEXTS.colId, field: "id", filter: true, width: 190 },
      {
        headerName: DELIVERY_ZONE_TEXTS.colMunicipality,
        field: "municipality",
        filter: true,
        flex: 1,
        editable: true,
      },
      {
        headerName: DELIVERY_ZONE_TEXTS.colZone,
        field: "zone",
        filter: true,
        width: 130,
        editable: true,
      },
      {
        headerName: DELIVERY_ZONE_TEXTS.colFee,
        field: "fee_cop",
        width: 130,
        editable: true,
        type: "numericColumn",
        valueParser: numberParser,
        valueFormatter: (params) => formatCOP(toNumber(params.value)),
      },
      {
        headerName: DELIVERY_ZONE_TEXTS.colThreshold,
        field: "free_shipping_threshold_cop",
        width: 170,
        editable: true,
        type: "numericColumn",
        valueParser: nullableNumberParser,
        valueFormatter: (params) =>
          params.value === null || params.value === undefined
            ? EMPTY_CELL
            : formatCOP(toNumber(params.value)),
      },
      {
        headerName: DELIVERY_ZONE_TEXTS.colEtaMin,
        field: "eta_days_min",
        width: 110,
        editable: true,
        type: "numericColumn",
        valueParser: numberParser,
      },
      {
        headerName: DELIVERY_ZONE_TEXTS.colEtaMax,
        field: "eta_days_max",
        width: 110,
        editable: true,
        type: "numericColumn",
        valueParser: numberParser,
      },
      {
        headerName: DELIVERY_ZONE_TEXTS.colOrder,
        field: "display_order",
        width: 100,
        editable: true,
        type: "numericColumn",
        valueParser: numberParser,
      },
      {
        headerName: DELIVERY_ZONE_TEXTS.colActive,
        field: "is_active",
        width: 100,
        editable: true,
        cellEditor: "agCheckboxCellEditor",
        cellRenderer: "agCheckboxCellRenderer",
        cellStyle: (params) => ({ color: params.value ? GREEN : RED, fontWeight: "bold" }),
      },
      {
        headerName: DELIVERY_ZONE_TEXTS.colActions,
        width: 120,
        pinned: "right",
        sortable: false,
        filter: false,
        cellRenderer: (params: { data: DeliveryZone }) => (
          <>
            <IconButton
              size="small"
              color="success"
              data-action="save"
              disabled={!params.data.id || !editedRows.has(params.data.id)}
              onClick={(e) => {
                e.stopPropagation();
                void handleSaveRow(params.data);
              }}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              data-action="delete"
              onClick={(e) => {
                e.stopPropagation();
                setZoneToDelete(params.data);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        ),
      },
    ],
    [editedRows, handleSaveRow],
  );

  const onCellValueChanged = useCallback((event: CellValueChangedEvent<DeliveryZone>) => {
    const id = event.data?.id;
    if (id) setEditedRows((prev) => new Set(prev).add(id));
  }, []);

  const handleSeed = async () => {
    try {
      const result = await seedZones().unwrap();
      setSuccessMsg(DELIVERY_ZONE_TEXTS.seedSuccess(result.created, result.skipped));
    } catch {
      setErrorMsg(DELIVERY_ZONE_TEXTS.seedError);
    }
  };

  const handleCreate = async (zone: DeliveryZone) => {
    try {
      await createZone(zone).unwrap();
      setCreateOpen(false);
      setSuccessMsg(DELIVERY_ZONE_TEXTS.createSuccess);
    } catch (err) {
      const status = (err as { status?: number })?.status;
      setErrorMsg(
        status === HTTP_CONFLICT
          ? DELIVERY_ZONE_TEXTS.createErrorDuplicate
          : DELIVERY_ZONE_TEXTS.createError,
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!zoneToDelete?.id) return;
    try {
      await deleteZone(zoneToDelete.id).unwrap();
      setSuccessMsg(DELIVERY_ZONE_TEXTS.deleteSuccess);
    } catch {
      setErrorMsg(DELIVERY_ZONE_TEXTS.deleteError);
    } finally {
      setZoneToDelete(null);
    }
  };

  const handleSaveConfig = async (patch: DeliveryConfig) => {
    setIsSavingConfig(true);
    try {
      await updateZone({ id: DELIVERY_CONFIG_ID, patch }).unwrap();
      setSuccessMsg(DELIVERY_ZONE_TEXTS.configSaveSuccess);
    } catch (err) {
      // The row is conditional on `attribute_exists(id)`, so a 404 is specifically "this stage was
      // never seeded" — an instruction, not a failure.
      const status = (err as { status?: number })?.status;
      setErrorMsg(
        status === HTTP_NOT_FOUND
          ? DELIVERY_ZONE_TEXTS.configMissing
          : DELIVERY_ZONE_TEXTS.configSaveError,
      );
    } finally {
      setIsSavingConfig(false);
    }
  };

  // The seed is offered only when there is nothing to lose. It is conditional per row and safe to
  // press twice, but a button that rewrites the tariff table has no business sitting next to a
  // table the business has already corrected.
  const showSeedButton = !isFetching && (data?.zones.length ?? 0) === 0;

  return (
    <PageLayout fullWidth showSearch={false}>
      <PageContentBox>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {DELIVERY_ZONE_TEXTS.pageTitle}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              placeholder={DELIVERY_ZONE_TEXTS.searchPlaceholder}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5 }} /> }}
            />
            {showSeedButton && (
              <Button variant="outlined" color="primary" disabled={isSeeding} onClick={handleSeed}>
                {isSeeding ? DELIVERY_ZONE_TEXTS.seeding : DELIVERY_ZONE_TEXTS.seedButton}
              </Button>
            )}
            <Button variant="contained" color="primary" onClick={() => setCreateOpen(true)}>
              {DELIVERY_ZONE_TEXTS.createButton}
            </Button>
          </Box>
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {DELIVERY_ZONE_TEXTS.loadError}
          </Alert>
        )}

        <div className="ag-theme-quartz" style={{ width: "100%", height: TABLE_HEIGHT }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            quickFilterText={searchText}
            overlayLoadingTemplate={`<span>${DELIVERY_ZONE_TEXTS.loading}</span>`}
            overlayNoRowsTemplate={`<span>${DELIVERY_ZONE_TEXTS.noRows}</span>`}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit
            stopEditingWhenCellsLoseFocus
          />
        </div>

        <DeliveryConfigCard
          config={data?.config}
          isSaving={isSavingConfig}
          onSave={handleSaveConfig}
        />
      </PageContentBox>

      <CreateZoneDialog
        open={createOpen}
        zoneOptions={zoneOptions}
        isCreating={isCreating}
        onCreate={handleCreate}
        onCancel={() => setCreateOpen(false)}
      />

      <DeleteZoneDialog
        open={zoneToDelete !== null}
        municipality={zoneToDelete?.municipality ?? ""}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setZoneToDelete(null)}
      />

      <Snackbar
        open={!!successMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setSuccessMsg("")}
      >
        <Alert severity="success" onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setErrorMsg("")}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default DeliveryZoneListPage;
